import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { PAGINATION } from '@/constants/pagination.constant';
import { transformerArray, transformerObject } from '../transformer';
import { Message } from '@/enums/message.enum';
import { DESCRIPTION, MESSAGE } from '@/constants/message.constant';

type AdminSliceState = {
  loading: boolean;
  listUserAdmin: User[];
  userAdminDetail?: UserAdminDetail;
  listActivityLogsUserAdmin: ActivityLog[];
  message?: MessageType;
  pagination: Pagination;
};

const initialState: AdminSliceState = {
  loading: false,
  listUserAdmin: [],
  listActivityLogsUserAdmin: [],
  pagination: PAGINATION
};

export const adminUserSlice = createAppSlice({
  name: 'admin',
  initialState,
  reducers: (create) => ({
    getListAdminUser: create.asyncThunk(
      async (query?: Record<string, any>) => {
        const response = await apiFetcher.get('user/admin', { query });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listUserAdmin = transformerArray(action.payload.listData);
          state.pagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listUserAdmin = [];
          state.pagination = PAGINATION;
        }
      }
    ),
    createAdminUser: create.asyncThunk(
      async ({ email, roleIds }: CreateAdminUserProps) => {
        await apiFetcher.post('auth/register', {
          email,
          password: '123456',
          roleIds: JSON.stringify(roleIds)
        });
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_ADMIN_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_ADMIN_FAIL,
            description: DESCRIPTION.ADMIN_USER_EXIST
          };
        }
      }
    ),
    updateAdminUser: create.asyncThunk(
      async ({ id, roleIds }: { id: string; roleIds: number[] }) => {
        const response = await apiFetcher.post(`user/admin/update`, {
          id,
          roleIds: JSON.stringify(roleIds)
        });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_ADMIN_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_ADMIN_FAIL
          };
        }
      }
    ),
    getAdminUserById: create.asyncThunk(
      async (id: string) => {
        const response = await apiFetcher.post(`user/admin/detail`, {
          id
        });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.userAdminDetail = transformerObject(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.userAdminDetail = undefined;
        }
      }
    ),
    deleteAdminUser: create.asyncThunk(
      async (id: number) => {
        await apiFetcher.post(`user/admin/delete`, { id });
        return id;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listUserAdmin = state.listUserAdmin.filter(
            (user) => user.id !== action.payload
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_ADMIN_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_ADMIN_FAIL
          };
        }
      }
    ),
    resetMessages: create.reducer((state) => {
      state.message = undefined;
    }),
    getListActivityLogsUserAdmin: create.asyncThunk(
      async (query?: Record<string, any>) => {
        const response = await apiFetcher.post('user/admin/active-logs', {
          ...query
        });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listActivityLogsUserAdmin = transformerArray(
            action.payload.listData
          );
          state.pagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listActivityLogsUserAdmin = [];
          state.pagination = PAGINATION;
        }
      }
    )
  }),

  selectors: {
    watchListAdminUser: (admin) => admin.listUserAdmin,
    watchAdminUserDetail: (admin) => admin.userAdminDetail,
    watchAdminUserMessage: (admin) => admin.message,
    watchAdminUserLoading: (admin) => admin.loading,
    watchListActivityLogsUserAdmin: (admin) => admin.listActivityLogsUserAdmin,
    watchAdminUserPagination: (admin) => admin.pagination
  }
});

export const {
  watchListAdminUser,
  watchAdminUserMessage,
  watchAdminUserLoading,
  watchAdminUserPagination,
  watchAdminUserDetail,
  watchListActivityLogsUserAdmin
} = adminUserSlice.selectors;

export const {
  getListAdminUser,
  createAdminUser,
  resetMessages,
  getAdminUserById,
  updateAdminUser,
  deleteAdminUser,
  getListActivityLogsUserAdmin
} = adminUserSlice.actions;
